/**
 * Meta Conversions API attribution helpers. All reads are consent-aware
 * at the output boundary (`getMetaSnapshot`), not at capture time.
 *
 * ─── Design (2026-10-29) ───────────────────────────────────────────────
 *
 * - Capture at app entry: `captureFbclidOnEntry()` reads `?fbclid=` from
 *   the landing URL and writes the raw value to sessionStorage. Lifecycle
 *   matches the UTM pattern (session-scoped, cleared on tab close).
 *   Mirrors the posthog.ts `has_fbclid` boolean capture — the two are
 *   independent: posthog keeps a boolean super property, this module
 *   keeps the raw value for Meta plumbing.
 *
 * - Read at checkout: `getMetaSnapshot()` returns
 *   { consentMarketing, fbc, fbp } where
 *     - consentMarketing = Cookiebot.consent.marketing === true
 *     - fbc = `_fbc` cookie if present, else synthesised from the
 *       sessionStorage fbclid as `fb.1.<ms>.<fbclid>` (Meta's documented
 *       format)
 *     - fbp = `_fbp` cookie if present
 *   fbc / fbp are returned verbatim regardless of consent — the backend
 *   gates persistence on `consentMarketing === true`. The CALLER
 *   (BestellungReviewScreen) decides whether to forward them.
 *
 * - No DOM mutations. No cookie writes. No network calls.
 * - Never throws.
 */

/**
 * SessionStorage key under which the raw `fbclid` landing-URL value is
 * stashed. Session-scoped — cleared when the tab closes.
 *
 * Exported ONLY for test setup; production callers should go through
 * `captureFbclidOnEntry` / `getMetaSnapshot`.
 */
export const FBCLID_SESSION_STORAGE_KEY = 'dreamable_fbclid';

/**
 * Call once on app entry (parallel to captureUtmsFromUrl). Reads
 * `?fbclid=` from the current URL and writes it to sessionStorage.
 * First-touch-wins: if the key already has a value, do nothing.
 *
 * Idempotent + no-op outside the browser. Never throws.
 */
export function captureFbclidOnEntry(): void {
  if (typeof window === 'undefined') return;
  try {
    const existing = window.sessionStorage.getItem(FBCLID_SESSION_STORAGE_KEY);
    if (typeof existing === 'string' && existing.length > 0) return;
    const params = new URLSearchParams(window.location.search);
    const raw = params.get('fbclid');
    if (typeof raw !== 'string' || raw.length === 0) return;
    window.sessionStorage.setItem(FBCLID_SESSION_STORAGE_KEY, raw);
  } catch {
    // sessionStorage can throw in private-browse Safari + ITP corners.
    // Silent swallow — missing fbclid is a degraded-attribution case,
    // not a user-visible defect.
  }
}

export interface MetaSnapshot {
  readonly consentMarketing: boolean;
  readonly fbc: string | null;
  readonly fbp: string | null;
}

/**
 * Read Cookiebot's `marketing` category + `_fbc` / `_fbp` cookies, and
 * synthesise `_fbc` from a stored fbclid when the cookie is absent.
 *
 * Returns the raw cookie / synthesised values regardless of consent;
 * the caller decides whether to forward. Backend persists fbc/fbp
 * only when `consentMarketing === true`.
 */
export function getMetaSnapshot(): MetaSnapshot {
  if (typeof window === 'undefined') {
    return { consentMarketing: false, fbc: null, fbp: null };
  }
  const consentMarketing = window.Cookiebot?.consent?.marketing === true;
  const fbp = readCookie('_fbp');
  const fbcFromCookie = readCookie('_fbc');
  const fbc = fbcFromCookie !== null ? fbcFromCookie : buildFbcFromStoredFbclid();
  return { consentMarketing, fbc, fbp };
}

/**
 * Parse `document.cookie` for a single cookie by name. Returns the raw
 * (URL-decoded) value or null when the cookie is absent / empty.
 *
 * Note: document.cookie formats as `name=value; name=value`. Values
 * with `=` inside (Meta's fbc/fbp are such — `fb.1.<ts>.<rand>`) are
 * handled by splitting on the FIRST `=` only.
 */
function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  try {
    const entries = document.cookie.split(';');
    for (const entry of entries) {
      const trimmed = entry.trim();
      if (trimmed.length === 0) continue;
      const eq = trimmed.indexOf('=');
      if (eq < 0) continue;
      const key = trimmed.slice(0, eq);
      if (key !== name) continue;
      const raw = trimmed.slice(eq + 1);
      if (raw.length === 0) return null;
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Build Meta's `_fbc` cookie format (`fb.1.<ms>.<fbclid>`) from a
 * previously-captured fbclid landing param. Returns null when no
 * fbclid is stored.
 *
 * The `fb.1.` prefix is Meta's documented format (version 1, subdomain
 * index 1 = app subdomain). The timestamp is the current ms epoch —
 * Meta's docs allow "the time of the click", approximated here as
 * "the time of checkout" since we did not capture the click time.
 */
function buildFbcFromStoredFbclid(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const fbclid = window.sessionStorage.getItem(FBCLID_SESSION_STORAGE_KEY);
    if (typeof fbclid !== 'string' || fbclid.length === 0) return null;
    return `fb.1.${Date.now()}.${fbclid}`;
  } catch {
    return null;
  }
}
